import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import formatPrice from '@honda-canada/js-utilities/lib/formatPrice';
import React, { useContext, useEffect } from 'react';
import { Box, Copy, H6, Icon, Media } from '@honda-canada/design-system-react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CommonModal from '../../CommonModal/CommonModal';
import Context from '../service/Context';
import ProtectionCard from './ProtectionCard';
import gtmConfiguration from '../../../core/gtm/gtmConfiguration';
import themeStyles from '../AccessoriesCardPanel.styles';
import useBapRegisterUserInteraction from '../../../utils/hooks/bap/useBapRegisterUserInteraction';
import useProtectionData from '../hooks/useProtectionData';
import { EqualHeight } from '../../../utils/components/EqualHeight';
import { getProtectionPrice } from '../utils/utils';
import { primaryCalculatorId, selectTermAndRate } from '../../../core/reducers/financialCalculators';
import { removeAllTagsFromMarkdown } from '../../../utils/markdown';
import { selectBodyTypesForGtm, selectCurrentVehicleFromVehicles } from '../../../core/reducers/vehicles';
import { selectCurrentVehicle, toggleProtection } from '../../../core/reducers/currentVehicle';
import { useAppSelector } from '../../../core/utils';
import { useLanguage } from '../../../utils/sitecoreContext';
const InfoBox = themeStyles.apply(Box, 'InfoBox');
const ProtectionCardWrapper = themeStyles.apply(Box, 'ProtectionCardWrapper');
const ModalContentWrapper = themeStyles.apply(Box, 'ModalContentWrapper');
const ModalCardsWrapper = themeStyles.apply(Box, 'ModalCardsWrapper');
const convertItemArray = (items) => (items.length === 1 ? items[0] : items);
const ProtectionExcludeModal = ({ componentName }) => {
    const dispatch = useDispatch();
    const language = useLanguage();
    const { registerUserInteraction } = useBapRegisterUserInteraction('config');
    const { protections } = useProtectionData();
    const { showModal, setShowModal, itemToAdd, itemsToRemove } = useContext(Context);
    const { t } = useTranslation();
    const gtmBodyStyle = useAppSelector(selectBodyTypesForGtm);
    const currentVehicle = useAppSelector(selectCurrentVehicleFromVehicles);
    const { inventoryStatus: availabilityFlag } = useAppSelector(selectCurrentVehicle);
    const addItem = protections.find(({ key }) => key === itemToAdd);
    const removeItems = protections.filter(({ key }) => itemsToRemove === null || itemsToRemove === void 0 ? void 0 : itemsToRemove.includes(key));
    const termAndRate = useAppSelector(state => selectTermAndRate(state, primaryCalculatorId));
    const addItemPrice = (addItem === null || addItem === void 0 ? void 0 : addItem.defaultAmount)
        ? getProtectionPrice(addItem.defaultAmount, addItem.terms, termAndRate)
        : 0;
    const diffPrice = addItemPrice -
        removeItems.reduce((acc, item) => acc + ((item === null || item === void 0 ? void 0 : item.defaultAmount) ? getProtectionPrice(item.defaultAmount, item.terms, termAndRate) : 0), 0);
    const gtmPayload = {
        model: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelGtmName,
        trim: removeAllTagsFromMarkdown(currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.trimGtmName),
        gtmBodyStyle,
        itemCategory: 'Protection',
    };
    const toBeRemovedTitles = Array.isArray(removeItems)
        ? removeItems.map((item) => item.titleEn)
        : (removeItems === null || removeItems === void 0 ? void 0 : removeItems.titleEn) || '';
    const currentSelection = (addItem === null || addItem === void 0 ? void 0 : addItem.titleEn) || '';
    const commonPayload = {
        ...gtmPayload,
        currentSelection,
        componentType: componentName,
        availabilityFlag,
        toBeRemoved: convertItemArray(toBeRemovedTitles),
        toBeAdded: (addItem === null || addItem === void 0 ? void 0 : addItem.titleEn) || '',
    };
    useEffect(() => {
        if (!showModal)
            return;
        gtmConfiguration({
            ...commonPayload,
            action: 'open',
        });
    }, [showModal]);
    const handleCloseModal = () => {
        setShowModal(false);
        gtmConfiguration({
            ...commonPayload,
            action: 'cancel',
        });
    };
    const handleModalConfirm = () => {
        registerUserInteraction();
        dispatch(toggleProtection(itemToAdd));
        itemsToRemove.forEach((itemId) => dispatch(toggleProtection(itemId)));
        setShowModal(false);
        gtmConfiguration({
            ...commonPayload,
            action: 'confirm',
        });
    };
    if (!(protections === null || protections === void 0 ? void 0 : protections.length))
        return null;
    if (!(addItem === null || addItem === void 0 ? void 0 : addItem.key))
        return null;
    return (_jsx(CommonModal, { handleCloseModal: handleCloseModal, handleSubmit: handleModalConfirm, showModal: showModal, title: _jsxs(_Fragment, { children: [_jsx("span", { "data-testid": "cy-bap-protection-modal-title", children: t('Pages.Bap.configChangeHeading') }), _jsx(Media, { greaterThan: "mobile", children: _jsx(Box, { "data-testid": "cy-bap-protection-modal-title-plusLogo", pr: "xs", children: _jsx(Icon, { name: "plusLogo" }) }) })] }), submitButtonEnabled: true, primaryButtonText: t('Pages.Bap.removeButton'), children: _jsxs(ModalContentWrapper, { "data-testid": "cy-bap-protection-modal-content", children: [_jsx(Media, { at: "mobile", children: _jsx(Box, { "data-testid": "cy-bap-protection-modal-title-plusLogo", pb: "default", pt: "xs", children: _jsx(Icon, { name: "plusLogo" }) }) }), _jsx(ModalCardsWrapper, { "data-body-scroll-lock-ignore": true, children: _jsxs(EqualHeight, { children: [_jsx(Copy, { size: "regular", "data-testid": "cy-CommonModal-subtitle", pb: "xs", children: t('Pages.Bap.protectionExclusionText') }), _jsx(H6, { "data-testid": "cy-bap-protection-modal-yourSelectionLabel", mb: "xs", children: t('Pages.Bap.yourSelectionLabel') }), _jsx(ProtectionCardWrapper, { "data-testid": "cy-bap-protection-modal-add-item", children: _jsx(ProtectionCard, { componentName: componentName, id: addItem.key, name: addItem.title, ...addItem, hideSelectedButton: true, hideVideo: true, preventScrollOnExpand: true }) }), _jsx(H6, { "data-testid": "cy-bap-protection-modal-itemsToBeRemovedLabel", mb: "xs", children: t('Pages.Bap.itemsToBeRemovedLabel') }), _jsx(ProtectionCardWrapper, { "data-testid": "cy-bap-protection-modal-remove-items", children: removeItems.map((item) => (_jsx(ProtectionCard, { id: item.key, name: item.title, ...item, hideSelectedButton: true, hideVideo: true, preventScrollOnExpand: true }, item.key))) })] }) }), _jsxs(InfoBox, { children: [_jsx(Copy, { size: "regular", pr: "8px", maxWidth: "75%", children: t('Pages.Bap.priceChangeLabel') }), _jsx(Copy, { fontWeight: "bold", size: "regular", "data-testid": "cy-bap-protection-modal-difference-price", children: `${diffPrice > 0 ? '+' : ''}${formatPrice(diffPrice, language, 2)}` })] })] }) }));
};
export default ProtectionExcludeModal;
